import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Context for the Permission Debug Overlay developer tool.
 * Allows developers to visualize which UI elements are protected by which permissions.
 *
 * Only available in development mode or when ENABLE_DEVELOPER_CONSOLE is true.
 */

const STORAGE_KEY = 'dev:permission-overlay';

interface PermissionOverlayState {
  enabled: boolean;
  showUnauthorized: boolean;
}

interface PermissionOverlayContextValue {
  /** Whether the permission overlay is currently enabled */
  enabled: boolean;
  /** Whether to show unauthorized elements (dimmed with red border) */
  showUnauthorized: boolean;
  /** Toggle the overlay on/off */
  toggleEnabled: () => void;
  /** Toggle showing unauthorized elements */
  toggleShowUnauthorized: () => void;
  /** Set enabled state directly */
  setEnabled: (enabled: boolean) => void;
  /** Set showUnauthorized state directly */
  setShowUnauthorized: (show: boolean) => void;
}

const defaultState: PermissionOverlayState = {
  enabled: false,
  showUnauthorized: false
};

const PermissionOverlayContext = createContext<PermissionOverlayContextValue>({
  enabled: false,
  showUnauthorized: false,
  toggleEnabled: () => {},
  toggleShowUnauthorized: () => {},
  setEnabled: () => {},
  setShowUnauthorized: () => {}
});

/**
 * Load state from localStorage
 */
function loadState(): PermissionOverlayState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        enabled: parsed.enabled ?? false,
        showUnauthorized: parsed.showUnauthorized ?? false
      };
    }
  } catch {
    // Ignore parse errors
  }
  return defaultState;
}

/**
 * Save state to localStorage
 */
function saveState(state: PermissionOverlayState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}

interface PermissionOverlayProviderProps {
  children: ReactNode;
}

/**
 * Provider for the Permission Overlay context.
 * Wrap your app with this to enable the permission debug overlay feature.
 */
export function PermissionOverlayProvider({ children }: PermissionOverlayProviderProps) {
  const [state, setState] = useState<PermissionOverlayState>(loadState);

  // Check if dev console should be available
  const env = (window as any).env;
  const isDevConsoleEnabled = process.env.NODE_ENV === 'development' || env?.ENABLE_DEVELOPER_CONSOLE === 'true';

  // Persist state to localStorage when it changes
  useEffect(() => {
    if (isDevConsoleEnabled) {
      saveState(state);
    }
  }, [state, isDevConsoleEnabled]);

  const toggleEnabled = useCallback(() => {
    if (!isDevConsoleEnabled) return;
    setState((prev) => ({ ...prev, enabled: !prev.enabled }));
  }, [isDevConsoleEnabled]);

  const toggleShowUnauthorized = useCallback(() => {
    if (!isDevConsoleEnabled) return;
    setState((prev) => ({ ...prev, showUnauthorized: !prev.showUnauthorized }));
  }, [isDevConsoleEnabled]);

  const setEnabled = useCallback(
    (enabled: boolean) => {
      if (!isDevConsoleEnabled) return;
      setState((prev) => ({ ...prev, enabled }));
    },
    [isDevConsoleEnabled]
  );

  const setShowUnauthorized = useCallback(
    (showUnauthorized: boolean) => {
      if (!isDevConsoleEnabled) return;
      setState((prev) => ({ ...prev, showUnauthorized }));
    },
    [isDevConsoleEnabled]
  );

  // Keyboard shortcut: Ctrl/Cmd + Shift + P to toggle overlay
  useEffect(() => {
    if (!isDevConsoleEnabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === 'p') {
        event.preventDefault();
        toggleEnabled();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDevConsoleEnabled, toggleEnabled]);

  const value = useMemo<PermissionOverlayContextValue>(
    () => ({
      enabled: isDevConsoleEnabled ? state.enabled : false,
      showUnauthorized: isDevConsoleEnabled ? state.showUnauthorized : false,
      toggleEnabled,
      toggleShowUnauthorized,
      setEnabled,
      setShowUnauthorized
    }),
    [state.enabled, state.showUnauthorized, toggleEnabled, toggleShowUnauthorized, setEnabled, setShowUnauthorized, isDevConsoleEnabled]
  );

  return <PermissionOverlayContext.Provider value={value}>{children}</PermissionOverlayContext.Provider>;
}

/**
 * Hook to access the Permission Overlay context.
 * @returns The permission overlay context value
 */
export function usePermissionOverlay(): PermissionOverlayContextValue {
  return useContext(PermissionOverlayContext);
}

export { PermissionOverlayContext };
